import { createApi, fetchBaseQuery, type BaseQueryFn, type FetchArgs, type FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { getAccessToken } from "../auth";
import { getSelectedOrgId } from "../api";

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "http://localhost:4100";

/**
 * `fetchBaseQuery` con headers de auth y X-Org-Id resueltos por request.
 * Mantiene compatibilidad con el sistema `apiGet/apiPost` legacy: si el token
 * o el org_id cambian (login/logout/cambiar org), las próximas queries usan el valor nuevo
 * automáticamente — RTK Query no cachea los headers.
 */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: BASE,
  prepareHeaders: async (headers) => {
    const token = await getAccessToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
    // Si el endpoint ya seteó X-Org-Id por-request (ej. el form de suscriptor
    // pide los servicios de la org elegida en el dialog), respetarlo.
    const orgId = getSelectedOrgId();
    if (orgId && !headers.has("X-Org-Id")) headers.set("X-Org-Id", orgId);
    return headers;
  },
});

/**
 * Wrapper que normaliza errores del backend al shape `{ code, message, status }`
 * que el resto del frontend ya espera (lo lanza el `ApiError` legacy).
 */
const baseQueryWithErrorShape: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const result = await rawBaseQuery(args, api, extraOptions);
  if (result.error) {
    const body = result.error.data as { error?: { code?: string; message?: string } } | undefined;
    return {
      error: {
        status: result.error.status,
        data: {
          code: body?.error?.code ?? "unknown",
          message: body?.error?.message ?? "Request failed",
        },
      } as FetchBaseQueryError,
    };
  }
  return result;
};

/**
 * Tags = "tablas lógicas" cacheadas por RTK Query. Cuando un mutation
 * `invalidatesTags: ["Services"]`, todas las queries con `providesTags: ["Services"]`
 * se refetchean automáticamente. Sumar nuevos tags acá cuando migramos otra página.
 */
export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithErrorShape,
  tagTypes: ["Services", "Subscribers", "Organizations", "OrgMembers", "Stats", "Users"],
  endpoints: () => ({}),
  // Refetch al volver a la pestaña — útil cuando el dashboard estuvo en background.
  refetchOnFocus: true,
  // Refetch al recuperar conexión.
  refetchOnReconnect: true,
});
